import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { KeyRound, LogOut, User } from "lucide-react";
import { Button } from "./ui/button";
import { LocaleSwitcher } from "./LocaleSwitcher";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { CALLER_QUERY_KEY, useCaller } from "../auth/useCaller";
import { signOut } from "../auth/session";

export function UserMenu() {
  const { t } = useTranslation();
  const { caller } = useCaller();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleSignOut = async () => {
    await signOut();
    await qc.invalidateQueries({ queryKey: CALLER_QUERY_KEY });
    navigate("/login", { replace: true });
  };

  if (!caller) return null;

  return (
    <div className="relative" ref={containerRef}>
      <Button variant="ghost" size="sm" onClick={() => setOpen((v) => !v)}>
        <User className="h-4 w-4" />
        <span className="hidden sm:inline">{caller.email ?? caller.sub}</span>
      </Button>
      {open && (
        <div className="absolute right-0 mt-2 w-64 rounded-md border border-border bg-popover shadow-lg z-50">
          <div className="px-3 py-2 border-b border-border">
            <p className="text-sm font-medium truncate">{caller.email ?? caller.sub}</p>
            {caller.groups.length > 0 && (
              <p className="text-xs text-muted-foreground">{caller.groups.join(", ")}</p>
            )}
          </div>
          <div className="px-3 py-2 border-b border-border flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{t("locale.label")}</span>
            <LocaleSwitcher />
          </div>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setChangePwOpen(true);
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent text-left border-b border-border"
          >
            <KeyRound className="h-4 w-4" />
            {t("auth.changePassword.menuLabel")}
          </button>
          <button
            type="button"
            onClick={handleSignOut}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-accent text-left"
          >
            <LogOut className="h-4 w-4" />
            {t("auth.signOut")}
          </button>
        </div>
      )}
      <ChangePasswordDialog open={changePwOpen} onOpenChange={setChangePwOpen} />
    </div>
  );
}
